import React, { useCallback, useMemo, useEffect } from "react";
import { useFormikContext } from "formik";
import { SelectField, FieldLabel, FieldValue, Badge, useConfirmModal } from "@td/ui";
import { translate } from "@td/shared_utils";
import { components } from "react-select";
import { fieldTypes, modalTypes } from "../constants";
import { PracticeTeamItem, AddPracticeButton, StatesContainer } from "../components";
import { usePracticeTeams } from "../contexts/practice-teams-context";
import { useProviderPanel } from '../contexts/provider-panel-context';
import { useModals } from "../contexts/modals-context";
import { map, filter, isEmpty } from "lodash";

const PracticeTeamField = React.memo(() => {
  const { openModal } = useModals();
  const { confirm } = useConfirmModal();
  const { values, setFieldValue } = useFormikContext();
  const practiceTeams = usePracticeTeams();
  const { provider, practiceTeamParticipant, isPrincipal } = useProviderPanel();

  const options = useMemo(() => {
    const { practiceTeamParticipants } = provider;
    const selectedPracticeTeams = map(practiceTeamParticipants, "practiceTeamId");

    return filter(practiceTeams, ({ value }) => !selectedPracticeTeams.includes(value));
  }, [practiceTeams, provider]);

  const handleClick = useCallback(() => {
    openModal(modalTypes.PROVIDER_PANEL_MODAL, { providerId: provider.id });
  }, [provider, openModal]);

  useEffect(() => {
    if (!isEmpty(values[fieldTypes.PRACTICE_TEAM]) && !practiceTeamParticipant && !isPrincipal) {
      const { availableParticipantStates } = provider;

      const { states } = practiceTeams.find(({ value }) => {
        return value === values[fieldTypes.PRACTICE_TEAM].value;
      });

      const availableStates = states.filter((state) => availableParticipantStates.includes(state));

      if (isEmpty(availableStates)) {
        return;
      }

      (async () => {
        const isConfirmed = await confirm({
          title: translate(null, "provider_panel.notifications", "states_apply_confirmation"),
          description: (
            <StatesContainer>
              {availableStates.map((state, index) => (
                <Badge key={index}>
                  {state}
                </Badge>
              ))}
            </StatesContainer>
          ),
        });

        if (isConfirmed) {
          setFieldValue(
            fieldTypes.STATES,
            availableStates.map((state) => ({
              value: state,
              label: translate(null, "states.state_codes", state),
            }))
          );
        }
      })();
    }
  }, [values[fieldTypes.PRACTICE_TEAM], provider, practiceTeamParticipant, isPrincipal, practiceTeams])

  return (
    <React.Fragment>
      {practiceTeamParticipant ? (
        <FieldLabel inline={true}>
          {translate(null, "provider_panel.form.fields.practice", "label")}*:

          <FieldValue>
            <PracticeTeamItem value={values[fieldTypes.PRACTICE_TEAM].label} />
          </FieldValue>

          <AddPracticeButton
            onClick={handleClick}
          />
        </FieldLabel>
      ) : (
        <SelectField
          hideSelectedOptions
          isSearchable={false}
          name={fieldTypes.PRACTICE_TEAM}
          options={options}
          label={`${translate(null, "provider_panel.form.fields.practice", "label")}*:`}
          placeholder={translate(null, "provider_panel.form.fields.practice", "placeholder")}
          components={{
            SingleValue: ({ data, ...props }) => (
              <components.SingleValue
                {...props}
                data={data}
              >
                <PracticeTeamItem
                  value={data.label}
                />
              </components.SingleValue>
            ),
          }}
        />
      )}
    </React.Fragment>
  );
});

PracticeTeamField.displayName = "PracticeTeamField";

export default PracticeTeamField;
